import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import "./AnikaAi.css";

/**
 * AnikaAi – centralised voice controller
 *
 * Props
 * ─────
 * voiceService          : voice service instance
 * autoStart             : boolean – fire once after AudioEnableModal "Yes" (first visit only)
 * introTexts            : string[] – hero/home section texts
 * onCallStateChange     : (isActive: boolean) => void
 * externalTrigger       : { section:'service'|'admin', texts:string[], id:number }
 * activeServiceFeature  : { name:string, index:number } | null  – synced with spiral animation
 * currentSection        : 'home'|'service'|'admin'|'other'      – which section user is in NOW
 *   ↑ parent passes this so manual "Talk to Anika" click knows what to speak
 * serviceFeatureTexts   : string[]  – all 14 feature names (for manual restart)
 * adminText             : string    – admin section text (for manual restart)
 */
const AnikaAi = ({
  voiceService,
  autoStart  = false,
  introTexts = [],
  onCallStateChange,
  externalTrigger,
  activeServiceFeature,
  currentSection,
  serviceFeatureTexts = [],
  adminText = '',
}) => {

  const [isCallActive,      setIsCallActive]      = useState(false);
  const [isSpeaking,        setIsSpeaking]        = useState(false);
  const [isToastVisible,    setIsToastVisible]    = useState(false);
  const [currentSpeechText, setCurrentSpeechText] = useState('');

  // ── refs ───────────────────────────────────────────────────────────────────
  const isCallActiveRef         = useRef(false);
  const currentSectionRef       = useRef(null);    // what Anika is currently narrating
  const adminSpokenRef          = useRef(false);
  const speechCheckRef          = useRef(null);
  const lastTriggerIdRef        = useRef(null);
  const narrationRunningRef     = useRef(false);
  const lastSpokenFeatureIndex  = useRef(-1);
  // tracks whether the full auto-tour has completed (home+service+admin all done)
  const tourCompleteRef         = useRef(false);
  // how many sections have auto-completed
  const sectionsCompleted       = useRef({ intro: false, service: false, admin: false });

  useEffect(() => { isCallActiveRef.current = isCallActive; }, [isCallActive]);

  // ── watch speechSynthesis ──────────────────────────────────────────────────
  useEffect(() => {
    speechCheckRef.current = setInterval(() => {
      setIsSpeaking(!!(window.speechSynthesis?.speaking));
    }, 200);
    return () => clearInterval(speechCheckRef.current);
  }, []);

  // ── pause on tab switch ────────────────────────────────────────────────────
  useEffect(() => {
    const onVisibility = () => {
      if (!isCallActiveRef.current) return;
      if (document.hidden) {
        window.speechSynthesis?.cancel();
        voiceService?.stop?.();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []); // eslint-disable-line

  // ── helpers ────────────────────────────────────────────────────────────────
  const stopSpeech = useCallback(() => {
    window.speechSynthesis?.cancel();
    voiceService?.stop?.();
  }, [voiceService]);

  const safespeak = useCallback(async (text) => {
    if (!voiceService || !text) return;
    while (document.hidden) await new Promise(r => setTimeout(r, 300));
    if (!isCallActiveRef.current) return;
    setCurrentSpeechText(text);
    await voiceService.speak(text);
  }, [voiceService]);

  // auto-stop when all 3 sections complete
  const checkTourComplete = useCallback(() => {
    const { intro, service, admin } = sectionsCompleted.current;
    if (intro && service && admin && !tourCompleteRef.current) {
      tourCompleteRef.current = true;
      // Give a closing line then stop
      setTimeout(async () => {
        if (!isCallActiveRef.current) return;
        await safespeak("That's everything! Feel free to explore or click me anytime to hear a section again.");
        // auto end call
        setIsCallActive(false);
        isCallActiveRef.current = false;
        setIsToastVisible(false);
        setCurrentSpeechText('');
        onCallStateChange?.(false);
        stopSpeech();
        narrationRunningRef.current = false;
      }, 300);
    }
  }, [safespeak, onCallStateChange, stopSpeech]);

  // ── INTRO ──────────────────────────────────────────────────────────────────
  const runIntro = useCallback(async (texts) => {
    narrationRunningRef.current = true;
    currentSectionRef.current   = 'intro';
    for (const t of texts) {
      if (!isCallActiveRef.current) { narrationRunningRef.current = false; return; }
      await safespeak(t);
      await new Promise(r => setTimeout(r, 400));
    }
    if (isCallActiveRef.current) setCurrentSpeechText('Anika is here. Scroll to explore!');
    narrationRunningRef.current = false;
    sectionsCompleted.current.intro = true;
    checkTourComplete();
  }, [safespeak, checkTourComplete]);

  // ── SERVICE PREAMBLE then feature ticks take over ─────────────────────────
  const runServiceEntry = useCallback(async (totalFeatures) => {
    narrationRunningRef.current       = true;
    currentSectionRef.current         = 'service';
    lastSpokenFeatureIndex.current    = -1;

    await safespeak("Now let me walk you through our service features.");
    if (!isCallActiveRef.current) { narrationRunningRef.current = false; return; }
    await new Promise(r => setTimeout(r, 300));
    await safespeak(`We have ${totalFeatures} powerful features for you.`);
    if (!isCallActiveRef.current) { narrationRunningRef.current = false; return; }
    await new Promise(r => setTimeout(r, 300));
    await safespeak("Let me introduce them one by one.");
    if (!isCallActiveRef.current) { narrationRunningRef.current = false; return; }

    narrationRunningRef.current = false;
    // feature ticks now drive speech via activeServiceFeature prop
  }, [safespeak]);

  // ── ADMIN ──────────────────────────────────────────────────────────────────
  const runAdminSpeech = useCallback(async (text) => {
    if (adminSpokenRef.current) return;
    narrationRunningRef.current = true;
    currentSectionRef.current   = 'admin';
    adminSpokenRef.current      = true;
    stopSpeech();
    await new Promise(r => setTimeout(r, 200));
    await safespeak(text);
    if (isCallActiveRef.current) setCurrentSpeechText('Click any feature icon to learn more!');
    narrationRunningRef.current = false;
    sectionsCompleted.current.admin = true;
    checkTourComplete();
  }, [safespeak, stopSpeech, checkTourComplete]);

  // ── activateCall UI ────────────────────────────────────────────────────────
  const activateCall = useCallback(() => {
    setIsCallActive(true);
    isCallActiveRef.current = true;
    setIsToastVisible(true);
    setCurrentSpeechText('Connecting to Anika AI...');
    onCallStateChange?.(true);
  }, [onCallStateChange]);

  // ── endCall ────────────────────────────────────────────────────────────────
  const endCall = useCallback(() => {
    setIsCallActive(false);
    isCallActiveRef.current = false;
    setIsToastVisible(false);
    setCurrentSpeechText('');
    onCallStateChange?.(false);
    stopSpeech();
    narrationRunningRef.current = false;
  }, [onCallStateChange, stopSpeech]);

  // ── MANUAL startCall: context-aware based on currentSection prop ───────────
  const startCall = useCallback(async () => {
    if (!voiceService) return;
    activateCall();
    await new Promise(r => setTimeout(r, 400));
    if (!isCallActiveRef.current) return;

    const where = currentSection; // 'home'|'service'|'admin'|'other'

    if (where === 'service') {
      // User is in service section → speak service (preamble + feature ticks)
      currentSectionRef.current      = 'service';
      lastSpokenFeatureIndex.current = -1;
      narrationRunningRef.current    = false;
      sectionsCompleted.current.service = false; // allow re-completing
      await runServiceEntry(serviceFeatureTexts.length || 14);
      return;
    }

    if (where === 'admin') {
      // User is in admin section → speak admin only
      adminSpokenRef.current = false; // allow re-speaking
      sectionsCompleted.current.admin = false;
      await runAdminSpeech(adminText);
      return;
    }

    // home or other → speak intro
    adminSpokenRef.current = false;
    lastSpokenFeatureIndex.current = -1;
    sectionsCompleted.current = { intro: false, service: false, admin: false };
    tourCompleteRef.current = false;
    await runIntro(introTexts);
  }, [voiceService, currentSection, activateCall, runIntro, runServiceEntry,
      runAdminSpeech, introTexts, serviceFeatureTexts, adminText]);

  // ── auto-start: fires ONCE after modal Yes, guarded by sessionStorage ──────
  useEffect(() => {
    if (!autoStart || introTexts.length === 0) return;

    // Guard: don't auto-start again on refresh
    const alreadyAutoStarted = sessionStorage.getItem('anikaAutoStarted');
    if (alreadyAutoStarted === 'true') return;

    sessionStorage.setItem('anikaAutoStarted', 'true');

    // Reset everything for a fresh tour
    sectionsCompleted.current   = { intro: false, service: false, admin: false };
    tourCompleteRef.current     = false;
    adminSpokenRef.current      = false;
    lastSpokenFeatureIndex.current = -1;
    currentSectionRef.current   = null;
    narrationRunningRef.current = false;
    lastTriggerIdRef.current    = null;

    startCall();
  }, [autoStart]); // eslint-disable-line

  // ── external trigger (scroll into section) ────────────────────────────────
  useEffect(() => {
    if (!externalTrigger) return;
    const { section, texts, id } = externalTrigger;

    if (id && id === lastTriggerIdRef.current) return;
    lastTriggerIdRef.current = id;

    const doActivate = async (fn) => {
      if (!isCallActiveRef.current) {
        activateCall();
        await new Promise(r => setTimeout(r, 400));
      }
      fn();
    };

    if (section === 'service' && currentSectionRef.current !== 'service') {
      lastSpokenFeatureIndex.current = -1;
      doActivate(() => runServiceEntry(texts.length));
    }

    if (section === 'admin') {
      doActivate(() => runAdminSpeech(texts[0]));
    }
  }, [externalTrigger]); // eslint-disable-line

  // ── feature tick: speak name that matches current spiral animation ─────────
  useEffect(() => {
    if (!activeServiceFeature) return;
    if (!isCallActiveRef.current) return;
    if (currentSectionRef.current !== 'service') return;
    if (narrationRunningRef.current) return;

    const { name, index } = activeServiceFeature;
    if (index === lastSpokenFeatureIndex.current) return;
    lastSpokenFeatureIndex.current = index;

    // If we just spoke the LAST feature, mark service as complete
    const isLast = serviceFeatureTexts.length > 0 && index === serviceFeatureTexts.length - 1;

    safespeak(name).then(() => {
      if (isLast && isCallActiveRef.current) {
        sectionsCompleted.current.service = true;
        checkTourComplete();
      }
    });
  }, [activeServiceFeature]); // eslint-disable-line

  // ── button toggle ──────────────────────────────────────────────────────────
  const handleToggle = () => {
    if (isCallActive) endCall();
    else startCall();
  };

  const truncatedText = currentSpeechText.length > 120
    ? currentSpeechText.substring(0, 120) + '...'
    : currentSpeechText;

  return (
    <>
      <button
        className={`new-anika-voiss-talk-btn ${isCallActive ? 'new-anika-voiss-talk-btn--active' : ''}`}
        onClick={handleToggle}
        title={isCallActive ? 'End Anika Call' : 'Talk to Anika'}
      >
        <div className={`new-anika-voiss-talk-btn-orb ${isCallActive ? 'new-anika-voiss-talk-btn-orb--active' : ''}`}>
          <div className="new-anika-voiss-talk-btn-orb-inner">
            {isCallActive ? (
              <DotLottieReact
                src="https://lottie.host/4af34a01-092b-43cb-8d27-ee8c80962655/PaJsSNG2l5.lottie"
                loop autoplay background="transparent" speed={1}
                style={{ width: '32px', height: '32px' }}
              />
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" x2="12" y1="19" y2="22" />
              </svg>
            )}
          </div>
        </div>
        <span className="new-anika-voiss-talk-btn-text">
          {isCallActive ? 'End Call' : 'Talk to Anika'}
        </span>
      </button>

      <div className={`new-anika-voiss-toast ${isToastVisible && isCallActive ? 'new-anika-voiss-toast--visible' : ''}`}>
        <div className="new-anika-voiss-toast-inner">
          <div className={`new-anika-voiss-toast-orb ${isSpeaking ? 'new-anika-voiss-toast-orb--speaking' : ''}`}>
            <div className="new-anika-voiss-toast-orb-glow"></div>
            <div className="new-anika-voiss-toast-orb-core">
              <DotLottieReact
                src="https://lottie.host/4af34a01-092b-43cb-8d27-ee8c80962655/PaJsSNG2l5.lottie"
                loop={isSpeaking} autoplay={isSpeaking}
                background="transparent" speed={1}
                style={{ width: '44px', height: '44px' }}
              />
            </div>
          </div>

          <div className="new-anika-voiss-toast-content">
            <div className="new-anika-voiss-toast-header">
              <span className="new-anika-voiss-talk-btn-text text-light">Anika AI</span>
              <span className={`new-anika-voiss-toast-status ${isSpeaking ? 'new-anika-voiss-toast-status--speaking' : ''}`}>
                {isSpeaking ? 'Speaking' : 'Connected'}
              </span>
            </div>
            <p className="new-anika-voiss-toast-text">{truncatedText}</p>
          </div>

          <button className="new-anika-voiss-toast-close" onClick={endCall} title="End Call">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" x2="6" y1="6" y2="18" />
              <line x1="6" x2="18" y1="6" y2="18" />
            </svg>
          </button>
        </div>

        {isSpeaking && (
          <div className="new-anika-voiss-toast-wave">
            {[...Array(20)].map((_, i) => (
              <div key={i} className="new-anika-voiss-toast-wave-bar"
                style={{ animationDelay: `${i * 0.05}s`, height: `${Math.random() * 12 + 3}px` }}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default AnikaAi;